import React from 'react';
import { LucideIcon } from 'lucide-react';

export interface EmptyStateProps {
  /** Decorative glyph above the title — usually the surface's domain icon. */
  icon?: LucideIcon;
  title?: string;
  /** One sentence of guidance: why it's empty, or what to do about it. */
  hint?: React.ReactNode;
  /** Optional CTA, e.g. a Button clearing filters or navigating away. */
  action?: React.ReactNode;
  /**
   * 'section' (default): an empty region inside a page — py-12, w-12 icon.
   * 'page': a whole page's content is the empty state — py-16.
   * 'compact': dense panels/dropdowns — py-8, w-8 icon, smaller text.
   */
  size?: 'page' | 'section' | 'compact';
  /** 'error' paints icon and title in the error color for failed loads. */
  tone?: 'neutral' | 'error';
  className?: string;
}

/**
 * The "nothing here" display, in one place.
 *
 * 48 files hand-rolled this — same centered icon/heading/hint stack with
 * drifting paddings (py-8/12/16), icon sizes (w-8/12/16), heading levels and
 * grays. Error variants ranged from bare red text to the full stack.
 *
 * Empty states are content, not chrome: the hint should say why the surface
 * is empty ("Try adjusting your filters." / "No measures have been entered
 * yet.") rather than restating the title. EmptyTableState remains the
 * in-table equivalent (it renders a <tr>).
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  hint,
  action,
  size = 'section',
  tone = 'neutral',
  className = '',
}) => {
  const pad = { page: 'py-16', section: 'py-12', compact: 'py-8' }[size];
  const iconSize = { page: 'w-12 h-12', section: 'w-12 h-12', compact: 'w-8 h-8' }[size];
  const titleSize = size === 'compact' ? 'text-sm' : 'text-lg';
  const hintSize = size === 'compact' ? 'text-xs' : '';

  const iconColor =
    tone === 'error'
      ? 'text-error-500 dark:text-error-400'
      : 'text-text-tertiary dark:text-text-dark-tertiary';
  const titleColor =
    tone === 'error'
      ? 'text-error-600 dark:text-error-400'
      : 'text-text-primary dark:text-text-dark-primary';

  return (
    <div className={`text-center ${pad} ${className}`}>
      {Icon && <Icon className={`${iconSize} ${iconColor} mx-auto mb-4`} aria-hidden="true" />}
      {title && (
        <h3 className={`${titleSize} font-medium ${titleColor} mb-2`}>{title}</h3>
      )}
      {hint && (
        <p className={`${hintSize} text-text-secondary dark:text-text-dark-secondary`}>{hint}</p>
      )}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
};

export default EmptyState;
